import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  ActivityIndicator,
  Modal,
  Pressable,
  Platform,
  Alert,
} from 'react-native';
import MaterialIcons from '@expo/vector-icons/MaterialIcons';
import * as ImagePicker from 'expo-image-picker';
import { useTheme } from '../context/ThemeContext';
import { getApiUrl } from '../utils/apiConfig';
import { uploadHandbookCommentImageFromPicker, uploadHandbookCommentYoutubeFromPicker } from '../utils/handbookCommentMediaUpload';
import YoutubeOAuthBar from './YoutubeOAuthBar';
import {
  formatYoutubeUploadError,
  isYoutubeReconnectError,
} from '../utils/youtubeBinaryUpload';
import { buildHandbookMoveOptions } from '../utils/handbookTimestamps';

/** Decorative rules above Comments — darker classic gold (not bright theme heading yellow in dark mode). */
const HANDBOOK_COMMENTS_RULE_GOLD = '#d4af37';

/**
 * @param {{
 *   entryId?: number,
 *   entrySlug?: string,
 *   studentNoteId?: number,
 *   formTitle?: string,
 *   noteTitle?: string,
 *   user: object,
 *   disabled?: boolean,
 *   onSuccess?: () => void,
 *   moveTimestamps?: string[],
 * }} props
 */
export default function HandbookEntryCommentComposer({
  entryId,
  entrySlug = '',
  studentNoteId,
  formTitle = '',
  noteTitle = '',
  user,
  disabled = false,
  onSuccess,
  moveTimestamps = [],
}) {
  const isStudentNote = studentNoteId != null;
  const parentId = isStudentNote ? studentNoteId : entryId;
  const displayTitle = isStudentNote ? noteTitle : formTitle;
  const theme = useTheme();
  const gold = theme.colors.heading;

  const [expanded, setExpanded] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [videoTitle, setVideoTitle] = useState('');
  const [pendingImage, setPendingImage] = useState(null);
  const [pendingVideo, setPendingVideo] = useState(null);
  const [fileBase64, setFileBase64] = useState(null);
  const [students, setStudents] = useState([]);
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [tagModalOpen, setTagModalOpen] = useState(false);
  const [moveModalOpen, setMoveModalOpen] = useState(false);
  const [selectedTagIds, setSelectedTagIds] = useState(() => new Set());
  const [saving, setSaving] = useState(false);
  const rosterFetchOnceRef = useRef(false);
  const youtubeOAuthRef = useRef(null);

  const styles = useMemo(() => getStyles(theme), [theme]);

  const moveOptions = useMemo(
    () => (isStudentNote ? [] : buildHandbookMoveOptions(moveTimestamps)),
    [isStudentNote, moveTimestamps]
  );

  const insertMoveReference = useCallback((opt) => {
    const line = `Move ${opt.index} ${opt.displayHms}`;
    setCommentText((t) => (t && t.trim() !== '' ? `${t.trimEnd()}\n${line}\n` : `${line}\n`));
    setMoveModalOpen(false);
  }, []);

  const loadStudents = useCallback(async () => {
    setStudentsLoading(true);
    try {
      const uid = user?.id != null ? encodeURIComponent(String(user.id)) : '';
      const requesterQ =
        user?.id != null ? `&requester_id=${encodeURIComponent(String(user.id))}` : '';
      const url = uid
        ? `${getApiUrl()}/api/students/for-tags?exclude_student_id=${uid}${requesterQ}`
        : `${getApiUrl()}/api/students/for-tags${requesterQ ? `?${requesterQ.slice(1)}` : ''}`;
      const r = await fetch(url);
      if (!r.ok) throw new Error('Could not load students');
      const rows = await r.json();
      setStudents(Array.isArray(rows) ? rows : []);
    } catch {
      setStudents([]);
    } finally {
      setStudentsLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    if (!expanded) {
      rosterFetchOnceRef.current = false;
      return;
    }
    if (rosterFetchOnceRef.current) return;
    rosterFetchOnceRef.current = true;
    loadStudents();
  }, [expanded, loadStudents]);

  const toggleTag = useCallback((id) => {
    setSelectedTagIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const clearPendingImage = useCallback(() => setPendingImage(null), []);
  const clearPendingVideo = useCallback(() => {
    setPendingVideo(null);
    setFileBase64(null);
    setVideoTitle('');
  }, []);

  const pickImage = async () => {
    try {
      const perm = await ImagePicker.requestMediaLibraryPermissionsAsync();
      if (!perm.granted) {
        Alert.alert('Permission', 'Allow photo library access to attach an image.');
        return;
      }
      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
        allowsMultipleSelection: false,
        quality: 0.92,
      });
      if (result.canceled || !result.assets?.[0]) return;
      const a = result.assets[0];
      setPendingVideo(null);
      setFileBase64(null);
      setVideoTitle('');
      setPendingImage({
        uri: a.uri,
        fileName: a.fileName || a.uri.split(/[/\\]/).pop() || 'image.jpg',
        mimeType: a.mimeType ?? null,
      });
    } catch (e) {
      Alert.alert('Image', e.message || 'Could not pick image.');
    }
  };

  const pickVideo = async () => {
    try {
      const perm = await ImagePicker.requestMediaLibraryPermissionsAsync();
      if (!perm.granted) {
        Alert.alert('Permission', 'Allow library access to attach a video.');
        return;
      }
      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.Videos,
        allowsMultipleSelection: false,
        quality: 1,
        base64: true,
      });
      if (result.canceled || !result.assets?.[0]) return;
      const a = result.assets[0];
      setPendingImage(null);
      const fileName = a.fileName || a.uri.split(/[/\\]/).pop() || 'video.mp4';
      setVideoTitle('');
      let b64 = a.base64;
      if (!b64) {
        const response = await fetch(a.uri);
        const blob = await response.blob();
        b64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            const s = reader.result;
            if (typeof s === 'string' && s.includes(',')) resolve(s.split(',')[1]);
            else reject(new Error('Could not read file'));
          };
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
      }
      setFileBase64(b64);
      setPendingVideo({ fileName });
    } catch (e) {
      Alert.alert('Video', e.message || 'Could not pick video.');
    }
  };

  const uploadHandbookImage = async () => {
    if (!pendingImage?.uri) return [];
    const supabase = getSupabaseClient();
    if (!supabase) {
      throw new Error(getSupabaseConfigHint().join('\n') || 'Supabase is not configured.');
    }
    const bucket = getCommentImagesBucketName();
    const { safe, contentType } = normalizeCourseImageForUpload(
      pendingImage.fileName,
      pendingImage.mimeType
    );
    const path = `entry_${entryId}_${Date.now()}_${safe}`;
    const body = await uriToArrayBuffer(pendingImage.uri);
    if (!body || body.byteLength === 0) throw new Error('Empty image file.');
    const { error } = await supabase.storage.from(bucket).upload(path, body, {
      contentType,
      upsert: false,
    });
    if (error) throw new Error(error.message || 'Image upload failed');
    const { data: pub } = supabase.storage.from(bucket).getPublicUrl(path);
    const url = pub?.publicUrl ? String(pub.publicUrl).trim().replace(/^http:\/\//i, 'https://') : '';
    if (!url) throw new Error('No public URL from storage');
    return [url];
  };

  const uploadYouTubeShort = async () => {
    if (!fileBase64 || !videoTitle.trim()) {
      throw new Error('Video title is required to upload.');
    }
    const up = await fetch(`${getApiUrl()}/api/youtube/upload-binary`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        fileBase64,
        title: videoTitle.trim(),
        description: `Handbook entry comment: ${formTitle || 'Chi Gung'}`,
        privacyStatus: 'unlisted',
        isShort: true,
        account_key: 'default',
      }),
    });
    const data = await up.json().catch(() => ({}));
    if (!up.ok) {
      const detail = formatYoutubeUploadError(data, 'YouTube upload failed');
      if (isYoutubeReconnectError(detail) || data.code === 'youtube_invalid_grant') {
        youtubeOAuthRef.current?.markNeedsReconnect?.(detail);
      }
      throw new Error(detail);
    }
    const y = data.youtubeUrl;
    if (!y) throw new Error('No YouTube URL returned');
    return [String(y).trim()];
  };

  const onSave = async () => {
    if (!user?.id || parentId == null) {
      Alert.alert('Sign in', 'You must be logged in to comment.');
      return;
    }
    const text = commentText.trim();
    if (!text && !pendingImage && !pendingVideo) {
      Alert.alert('Comment', 'Add a message or attach an image or video.');
      return;
    }
    if (pendingVideo && !videoTitle.trim()) {
      Alert.alert('Video title', 'Enter a name for the video before saving.');
      return;
    }

    setSaving(true);
    try {
      let newImageUrls = [];
      let newVideoUrls = [];

      console.log('[Comment save] start', {
        parentId,
        isStudentNote,
        hasText: !!text,
        hasPendingImage: !!pendingImage,
        hasPendingVideo: !!(pendingVideo && fileBase64),
        author_student_id: user.id,
        authorType: typeof user.id,
      });

      if (pendingImage) {
        newImageUrls = await uploadHandbookCommentImageFromPicker(
          isStudentNote ? { noteId: parentId, pendingImage } : { entryId: parentId, pendingImage }
        );
        console.log('[Comment save] after image upload', { count: newImageUrls.length });
      }
      if (pendingVideo && fileBase64) {
        newVideoUrls = await uploadHandbookCommentYoutubeFromPicker({
          fileBase64,
          videoTitle,
          formTitle: displayTitle,
          contextLabel: isStudentNote ? 'Student note comment' : 'Handbook entry comment',
        });
        console.log('[Comment save] after YouTube upload', { count: newVideoUrls.length });
      }

      const tagged_student_ids = [...selectedTagIds];

      const postUrl = isStudentNote
        ? `${getApiUrl()}/api/student-notes/${encodeURIComponent(String(parentId))}/comments`
        : `${getApiUrl()}/api/handbook-entries/${encodeURIComponent(String(parentId))}/comments`;
      const payload = {
        author_student_id: user.id,
        description: text,
        tagged_student_ids,
        new_image_urls: newImageUrls,
        new_video_urls: newVideoUrls,
        ...(isStudentNote ? {} : { entry_slug: entrySlug }),
      };
      console.log('[Handbook comment save] POST', postUrl, {
        new_image_urls_len: newImageUrls.length,
        new_video_urls_len: newVideoUrls.length,
      });

      const res = await fetch(postUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        console.error('[Handbook comment save] POST failed', {
          status: res.status,
          body,
          url: postUrl,
        });
        const detail = body.detail ? ` (${body.detail})` : '';
        const code = body.code ? ` [${body.code}]` : '';
        throw new Error((body.error || `Save failed (${res.status})`) + detail + code);
      }
      console.log('[Handbook comment save] ok', body?.comment?.id ?? body);

      setCommentText('');
      setVideoTitle('');
      setPendingImage(null);
      setPendingVideo(null);
      setFileBase64(null);
      setSelectedTagIds(new Set());
      /* Keep composer open so the student can post another comment on this form right away. */
      onSuccess?.();
      Alert.alert(
        'Saved',
        isStudentNote
          ? 'Comment posted. Notifications were sent where applicable.'
          : 'Comment posted and notifications sent to tagged students.'
      );
    } catch (e) {
      console.error('[Handbook comment save] caught', e?.message || e);
      Alert.alert('Save failed', e.message || 'Could not save comment.');
    } finally {
      setSaving(false);
    }
  };

  const tagLabel = selectedTagIds.size === 0 ? 'tag' : `tag (${selectedTagIds.size})`;

  return (
    <View
      style={styles.outer}
      {...(Platform.OS === 'web' ? { dataSet: { handbookCommentComposer: 'true' } } : {})}
    >
      <View style={styles.commentsGoldRulesAbove}>
        <View
          style={[styles.commentsGoldRule, { backgroundColor: HANDBOOK_COMMENTS_RULE_GOLD }]}
          {...(Platform.OS === 'web' ? { nativeID: 'handbook-comment-gold-rule-1' } : {})}
        />
        <View
          style={[styles.commentsGoldRuleSecond, { backgroundColor: HANDBOOK_COMMENTS_RULE_GOLD }]}
          {...(Platform.OS === 'web' ? { nativeID: 'handbook-comment-gold-rule-2' } : {})}
        />
      </View>
      <TouchableOpacity
        style={styles.headerRow}
        onPress={() => setExpanded((v) => !v)}
        activeOpacity={0.75}
        accessibilityRole="button"
        accessibilityLabel={expanded ? 'Collapse comments' : 'Add comment'}
        {...(Platform.OS === 'web' ? { nativeID: 'student-notes-comments-header' } : {})}
      >
        <Text style={[styles.commentsLabel, { color: gold }]}>Comments</Text>
        <MaterialIcons name="add-circle-outline" size={26} color={gold} />
      </TouchableOpacity>

      {expanded ? (
        <View style={styles.composerBox}>
          <TextInput
            style={[
              styles.textArea,
              {
                color: theme.colors.text,
                borderColor: theme.colors.border,
                backgroundColor: theme.isDarkMode ? theme.colors.surface : '#fafafa',
              },
            ]}
            placeholder="Write your comment…"
            placeholderTextColor={theme.isDarkMode ? '#888' : '#666'}
            multiline
            value={commentText}
            onChangeText={setCommentText}
            textAlignVertical="top"
          />

          {expanded ? (
            <YoutubeOAuthBar ref={youtubeOAuthRef} containerStyle={{ marginTop: 8 }} />
          ) : null}

          {pendingVideo ? (
            <TextInput
              style={[
                styles.videoNameInput,
                {
                  color: theme.colors.text,
                  borderColor: theme.colors.border,
                  backgroundColor: theme.isDarkMode ? theme.colors.surface : '#fafafa',
                },
              ]}
              placeholder="Video name (required)"
              placeholderTextColor={theme.isDarkMode ? '#888' : '#666'}
              value={videoTitle}
              onChangeText={setVideoTitle}
            />
          ) : null}

          <View style={styles.controlRow}>
            <TouchableOpacity
              style={[styles.iconBtn, { borderColor: gold }]}
              onPress={pickImage}
              disabled={saving}
              accessibilityLabel="Attach image"
            >
              <MaterialIcons name="image" size={22} color={gold} />
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.iconBtn, { borderColor: gold }]}
              onPress={pickVideo}
              disabled={saving}
              accessibilityLabel="Attach video"
            >
              <MaterialIcons name="videocam" size={22} color={gold} />
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.tagBtn, { borderColor: gold }]}
              onPress={() => setTagModalOpen(true)}
              disabled={saving || studentsLoading}
            >
              {studentsLoading ? (
                <ActivityIndicator size="small" color={gold} />
              ) : (
                <Text style={[styles.tagBtnText, { color: gold }]} numberOfLines={1}>
                  {tagLabel}
                </Text>
              )}
            </TouchableOpacity>
            {moveOptions.length > 0 ? (
              <TouchableOpacity
                style={[styles.moveBtn, { borderColor: gold }]}
                onPress={() => setMoveModalOpen(true)}
                disabled={saving}
                accessibilityRole="button"
                accessibilityLabel="Insert move reference"
              >
                <Text style={[styles.moveBtnText, { color: gold }]}>Move</Text>
                <MaterialIcons name="arrow-drop-down" size={18} color={gold} />
              </TouchableOpacity>
            ) : null}
            <TouchableOpacity
              style={styles.saveBtn}
              onPress={onSave}
              disabled={saving}
              {...(Platform.OS === 'web' ? { dataSet: { handbookCommentSave: 'true' } } : {})}
            >
              {saving ? (
                <ActivityIndicator size="small" color="#FFFFFF" />
              ) : (
                <Text style={styles.saveBtnText}>Save</Text>
              )}
            </TouchableOpacity>
          </View>

          {pendingImage ? (
            <View
              style={styles.fileRow}
              {...(Platform.OS === 'web' ? { dataSet: { handbookCommentPendingFile: 'true' } } : {})}
            >
              <Text style={[styles.fileName, { color: theme.colors.text }]} numberOfLines={1}>
                {pendingImage.fileName}
              </Text>
              <TouchableOpacity onPress={clearPendingImage} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
                <Text style={[styles.fileRemove, { color: gold }]}>✕</Text>
              </TouchableOpacity>
            </View>
          ) : null}

          {pendingVideo ? (
            <View
              style={styles.fileRow}
              {...(Platform.OS === 'web' ? { dataSet: { handbookCommentPendingFile: 'true' } } : {})}
            >
              <Text style={[styles.fileName, { color: theme.colors.text }]} numberOfLines={1}>
                {pendingVideo.fileName}
              </Text>
              <TouchableOpacity onPress={clearPendingVideo} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
                <Text style={[styles.fileRemove, { color: gold }]}>✕</Text>
              </TouchableOpacity>
            </View>
          ) : null}
        </View>
      ) : null}

      <Modal visible={tagModalOpen} transparent animationType="fade" onRequestClose={() => setTagModalOpen(false)}>
        <View style={styles.tagModalRoot}>
          <Pressable style={styles.modalBackdrop} onPress={() => setTagModalOpen(false)} />
          <View
            style={[styles.modalSheet, { backgroundColor: theme.colors.surface, borderColor: theme.colors.border }]}
          >
          <Text style={[styles.modalTitle, { color: theme.colors.heading }]}>Tag students</Text>
          <Text style={[styles.modalHint, { color: theme.colors.text, opacity: 0.8 }]}>
            All visible students (except you) — tap to select. They get a notification when you save.
          </Text>
          <ScrollView style={styles.modalList} keyboardShouldPersistTaps="handled">
            {students.map((s) => {
              const id = s.id;
              const label =
                String(s.student_name || '').trim() ||
                String(s.username || '').trim() ||
                `Student ${id}`;
              const on = selectedTagIds.has(id);
              return (
                <TouchableOpacity
                  key={id}
                  style={styles.modalRow}
                  onPress={() => toggleTag(id)}
                >
                  <MaterialIcons
                    name={on ? 'check-box' : 'check-box-outline-blank'}
                    size={22}
                    color={gold}
                  />
                  <Text style={[styles.modalRowText, { color: theme.colors.text }]}>{label}</Text>
                </TouchableOpacity>
              );
            })}
          </ScrollView>
          <TouchableOpacity style={styles.modalDone} onPress={() => setTagModalOpen(false)}>
            <Text style={styles.modalDoneText}>Done</Text>
          </TouchableOpacity>
          </View>
        </View>
      </Modal>

      <Modal visible={moveModalOpen} transparent animationType="fade" onRequestClose={() => setMoveModalOpen(false)}>
        <View style={styles.tagModalRoot}>
          <Pressable style={styles.modalBackdrop} onPress={() => setMoveModalOpen(false)} />
          <View
            style={[styles.modalSheet, { backgroundColor: theme.colors.surface, borderColor: theme.colors.border }]}
          >
            <Text style={[styles.modalTitle, { color: theme.colors.heading }]}>Move</Text>
            <Text style={[styles.modalHint, { color: theme.colors.text, opacity: 0.8 }]}>
              Insert a move label into your comment (same times as Jump to Move).
            </Text>
            <ScrollView style={styles.modalList} keyboardShouldPersistTaps="handled">
              {moveOptions.map((opt) => (
                <TouchableOpacity key={opt.index} style={styles.modalRow} onPress={() => insertMoveReference(opt)}>
                  <Text style={[styles.modalRowText, { color: theme.colors.text }]}>{opt.label}</Text>
                </TouchableOpacity>
              ))}
            </ScrollView>
            <TouchableOpacity style={styles.modalDone} onPress={() => setMoveModalOpen(false)}>
              <Text style={styles.modalDoneText}>Close</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>
    </View>
  );
}

function getStyles(theme) {
  return StyleSheet.create({
    outer: {
      marginTop: 28,
      alignSelf: 'stretch',
      width: '100%',
      maxWidth: '100%',
      paddingBottom: 8,
    },
    commentsGoldRulesAbove: {
      width: '100%',
      alignSelf: 'stretch',
      marginBottom: 14,
      flexShrink: 0,
    },
    /** Solid bars (not relying on CSS gap — RN / web sizing can swallow 2px Views). */
    commentsGoldRule: {
      width: '100%',
      minHeight: 3,
      height: 3,
      marginBottom: 8,
      borderRadius: 1,
    },
    commentsGoldRuleSecond: {
      width: '100%',
      minHeight: 3,
      height: 3,
      borderRadius: 1,
    },
    headerRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
      alignSelf: 'stretch',
      backgroundColor: 'transparent',
    },
    commentsLabel: {
      fontSize: 18,
      fontWeight: '700',
    },
    composerBox: {
      marginTop: 14,
      alignSelf: 'stretch',
    },
    textArea: {
      minHeight: 120,
      borderWidth: 1,
      borderRadius: 8,
      padding: 12,
      fontSize: 15,
      lineHeight: 22,
      marginBottom: 12,
    },
    videoNameInput: {
      borderWidth: 1,
      borderRadius: 8,
      paddingHorizontal: 12,
      paddingVertical: 10,
      fontSize: 15,
      marginBottom: 12,
    },
    controlRow: {
      flexDirection: 'row',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: 10,
    },
    iconBtn: {
      width: 44,
      height: 44,
      borderRadius: 8,
      borderWidth: 1.5,
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: 'transparent',
    },
    tagBtn: {
      flex: 1,
      minWidth: 72,
      height: 44,
      borderRadius: 8,
      borderWidth: 1.5,
      justifyContent: 'center',
      alignItems: 'center',
      paddingHorizontal: 8,
    },
    tagBtnText: {
      fontSize: 14,
      fontWeight: '600',
    },
    moveBtn: {
      flex: 1,
      minWidth: 72,
      height: 44,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 2,
      borderRadius: 8,
      borderWidth: 1.5,
      paddingHorizontal: 8,
    },
    moveBtnText: {
      fontSize: 14,
      fontWeight: '600',
    },
    saveBtn: {
      minWidth: 72,
      height: 44,
      borderRadius: 8,
      justifyContent: 'center',
      alignItems: 'center',
      paddingHorizontal: 16,
      backgroundColor: theme.colors.primary,
    },
    saveBtnText: {
      fontSize: 15,
      fontWeight: '700',
      color: '#FFFFFF',
    },
    fileRow: {
      flexDirection: 'row',
      alignItems: 'center',
      marginTop: 10,
      paddingVertical: 4,
      backgroundColor: 'transparent',
    },
    fileName: {
      flex: 1,
      fontSize: 13,
      marginRight: 8,
    },
    fileRemove: {
      fontSize: 18,
      fontWeight: '700',
      paddingHorizontal: 4,
    },
    modalBackdrop: {
      ...StyleSheet.absoluteFillObject,
      backgroundColor: 'rgba(0,0,0,0.45)',
    },
    tagModalRoot: {
      flex: 1,
      justifyContent: 'center',
      paddingHorizontal: 20,
    },
    modalSheet: {
      maxHeight: '75%',
      borderRadius: 12,
      borderWidth: 1,
      padding: 16,
      zIndex: 2,
    },
    modalTitle: {
      fontSize: 18,
      fontWeight: '700',
      marginBottom: 6,
    },
    modalHint: {
      fontSize: 13,
      lineHeight: 18,
      marginBottom: 12,
    },
    modalList: {
      maxHeight: 320,
    },
    modalRow: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingVertical: 10,
      gap: 10,
    },
    modalRowText: {
      fontSize: 15,
      flex: 1,
    },
    modalDone: {
      marginTop: 12,
      paddingVertical: 12,
      borderRadius: 8,
      alignItems: 'center',
      backgroundColor: theme.colors.primary,
    },
    modalDoneText: {
      fontSize: 16,
      fontWeight: '700',
      color: '#FFFFFF',
    },
  });
}
